import { useState } from 'react'
import toast from 'react-hot-toast'
import { Trans, useTranslation } from 'react-i18next'
import { IdleTimerProvider } from 'react-idle-timer'
import { useNavigate } from 'react-router'

import Button from '@/components/Button'
import FieldToggler from '@/components/FieldToggler'
import Icon from '@/components/Icon'
import Info from '@/components/Info'
import Pointer from '@/components/Pointer'
import Price from '@/components/Price'
import useLayout from '@/layout/useLayout'
import { CONFIRMATION_ERROR_TIMEOUT, CONFIRMATION_SUCCESS_TIMEOUT } from '@/lib/config'
import useTerminal from '@/lib/useTerminal'

import useCheckout from '../../hooks/useCheckout'
import Step from '../Step/Step'
import {
  ConfirmationContent,
  ConfirmationElapsed,
  ConfirmationElapsedIcon,
  ConfirmationInfo,
  ConfirmationInner,
  ConfirmationSummary
} from './Confirmation.styles'

const ConfirmationSuccess = ({ resetCheckout }: { resetCheckout: () => void }) => {
  const { t } = useTranslation()
  const { orderId, cartTotal, checkoutStartedAt } = useCheckout()
  const { accessibilityMode } = useLayout()
  const [printConfirmation, setPrintConfirmation] = useState(false)
  const [transactionElapsedSeconds] = useState<number | null>(() => {
    if (checkoutStartedAt === null) {
      return null
    }

    return Math.max(1, Math.round((Date.now() - checkoutStartedAt) / 1000))
  })
  const { printTestReceipt } = useTerminal()

  const printReceipt = () => {
    // TODO: Handle printing
    if (!orderId) {
      toast.error(t('printError'))
      return
    }

    void printTestReceipt().catch(error => {
      toast.error(t('printError'))
      console.error('[terminal] printTestReceipt error', error)
    })
  }

  const handleSubmit = () => {
    if (printConfirmation) {
      printReceipt()
    }
    resetCheckout()
  }

  return (
    <>
      <h2>{t('confirmationTitle')}</h2>
      <h3>{t('confirmationDescription')}</h3>
      {transactionElapsedSeconds !== null && (
        <ConfirmationElapsed>
          <ConfirmationElapsedIcon>
            <Icon name="clock" />
          </ConfirmationElapsedIcon>
          <span>
            <Trans i18nKey="transactionElapsedMessage" count={transactionElapsedSeconds} components={{ strong: <strong /> }} />
          </span>
        </ConfirmationElapsed>
      )}
      <ConfirmationInner>
        <ConfirmationSummary>
          <dl>
            <dt>{t('transactionNumber')}</dt>
            <dd>{orderId || '-'}</dd>
          </dl>
          <dl>
            <dt>{t('total')}</dt>
            <dd>
              <Price>{cartTotal}</Price>
            </dd>
          </dl>
          <dl>
            <dt>{t('status')}</dt>
            <dd>{t('paymentStatus.finished')}</dd>
          </dl>
          {/* {accessibilityMode && (
            <FieldToggler defaultValue={printConfirmation} onChange={value => setPrintConfirmation(value)} label={t('iWantReceipt')} />
          )} */}
        </ConfirmationSummary>
        <ConfirmationInfo>
          <Info>Kod QR do paragonu znajduje się na wydruku</Info>
        </ConfirmationInfo>
      </ConfirmationInner>
      {/* {!accessibilityMode && (
        <FieldToggler defaultValue={printConfirmation} onChange={value => setPrintConfirmation(value)} label={t('iWantReceipt')} />
      )} */}
      <Button size="lg" onClick={handleSubmit}>
        {t(printConfirmation ? 'printAndFinish' : 'finish')}
      </Button>
      <Pointer>{t('takeTickets')}</Pointer>
      <IdleTimerProvider
        events={['keydown', 'wheel', 'DOMMouseScroll', 'mousewheel', 'mousedown', 'touchstart', 'touchmove']}
        timeout={CONFIRMATION_SUCCESS_TIMEOUT}
        onIdle={resetCheckout}
      />
    </>
  )
}

const ConfirmationError = ({ resetCheckout }: { resetCheckout: () => void }) => {
  const { t } = useTranslation()
  const { paymentError, cartTotal, setCurrentStep, setPaymentError } = useCheckout()

  const handleBack = () => {
    setPaymentError(undefined)
    setCurrentStep('PAYMENT_METHOD')
  }

  return (
    <>
      <h2>{t('checkoutErrorTitle')}</h2>
      <h3>{t('checkoutErrorDescription')}</h3>
      <ConfirmationInner>
        <ConfirmationSummary>
          <dl>
            <dt>{t('total')}</dt>
            <dd>
              <Price>{cartTotal}</Price>
            </dd>
          </dl>
          <dl>
            <dt>{t('error')}</dt>
            <dd>{paymentError}</dd>
          </dl>
        </ConfirmationSummary>
      </ConfirmationInner>
      <Button onClick={handleBack}>{t('tryAgain')}</Button>
      <IdleTimerProvider
        events={['keydown', 'wheel', 'DOMMouseScroll', 'mousewheel', 'mousedown', 'touchstart', 'touchmove']}
        timeout={CONFIRMATION_ERROR_TIMEOUT}
        onIdle={resetCheckout}
      />
    </>
  )
}

const Confirmation = () => {
  const { paymentError } = useCheckout()
  const navigate = useNavigate()
  const { reset } = useCheckout()

  const resetCheckout = () => {
    navigate('/')
    setTimeout(() => {
      reset()
    }, 500)
  }

  return (
    <Step>
      <ConfirmationContent>
        {!paymentError && <ConfirmationSuccess resetCheckout={resetCheckout} />}
        {paymentError && <ConfirmationError resetCheckout={resetCheckout} />}
      </ConfirmationContent>
    </Step>
  )
}

export default Confirmation
